import React, { Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import Header from './components/Mainpage/Header';
import Footer from './components/Footer/Footer';
import AnnouncementBar from './components/Mainpage/AnnouncementBar';
import WhatsAppIcon from './components/WhatsApp/WhatsAppIcon';
import ScrollToTop from './components/Content/ScrollToTop';
import Loader from './components/Loader/Loader';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFound from './components/Content/NotFound';

// Landing is the entry point for nearly every visit, so it stays in the main
// bundle. Everything else is split out — this keeps `country-state-city`
// (several MB, used only by checkout and profile editing) off the critical path.
import Landingpage from './components/Landingpage/Landingpage';

const Home = lazy(() => import('./components/Content/Home'));
const HotProducts = lazy(() => import('./components/Content/HotProducts'));
const NewArrival = lazy(() => import('./components/Content/NewArrival'));
const AlsoLike = lazy(() => import('./components/Content/AlsoLike'));
const CategoryList = lazy(() => import('./components/Content/CategoryList'));
const Productshow = lazy(() => import('./components/Content/Productshow'));
const Showallreviews = lazy(() => import('./components/Content/Showallreviews'));
const BestReviews = lazy(() => import('./components/Content/BestReviews'));
const AllBestReviews = lazy(() => import('./components/Content/AllBestReviews'));
const AboutUs = lazy(() => import('./components/Content/AboutUs'));
const TAC = lazy(() => import('./components/Content/TAC'));
const Policy = lazy(() => import('./components/Content/Policy'));
const Policies = lazy(() => import('./components/Content/Policies'));
const Search = lazy(() => import('./components/Search/Search'));
const Wishlist = lazy(() => import('./components/Wishlist/Wishlist'));
const Check = lazy(() => import('./components/Private_routes/Check'));
const CheckOut = lazy(() => import('./components/CheckOut/CheckOut'));
const CheckOutHeader = lazy(() => import('./components/CheckOut/CheckOutHeader'));
const Profile = lazy(() => import('./components/Content/Profile'));
const YourOrders = lazy(() => import('./components/Content/Your_Orders'));
const Order = lazy(() => import('./components/Content/Order'));

function App() {
  const location = useLocation();
  const isCheckOutPage = location.pathname.startsWith('/check/checkout');

  return (
    <>
      <a className="ij-skip-link" href="#main-content">
        Skip to content
      </a>

      <WhatsAppIcon />
      <AnnouncementBar />
      <Header />
      <ScrollToTop />

      <main id="main-content" tabIndex={-1}>
        <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<Loader label="Loading page" />}>
            {isCheckOutPage && <CheckOutHeader />}
            <Routes>
              <Route path="/" element={<Landingpage />} />
              <Route path="/wishlist" element={<Wishlist />} />

              <Route path="/check/*" element={<Check />}>
                <Route path="checkoutpage" element={<CheckOut />} />
                <Route path="profile" element={<Profile />} />
                <Route path="profile/your-orders" element={<YourOrders />} />
                <Route
                  path="profile/your-orders/order/:order_id"
                  element={<Order />}
                />
              </Route>

              <Route path="/all-best-reviews" element={<AllBestReviews />} />
              <Route path="/tac" element={<TAC />} />
              <Route path="/aboutus" element={<AboutUs />} />
              <Route path="/policy" element={<Policy />} />
              <Route path="/policies" element={<Policies />} />

              <Route
                path="/productshow/:product_code"
                element={<Productshow />}
              />
              {/* Same screen, reached from a "you may also like" tile. The
                  key forces a fresh mount so state resets between products. */}
              <Route
                path="/productshowlike/:product_code"
                element={<Productshow key={location.pathname} />}
              />

              <Route path="/allproducts" element={<Home />} />
              <Route path="/category" element={<Home />} />
              <Route path="/product" element={<Home />} />
              <Route path="/all-categories" element={<CategoryList />} />
              <Route path="/hot-products" element={<HotProducts />} />
              <Route path="/new-products" element={<NewArrival />} />
              <Route path="/also-like" element={<AlsoLike />} />
              <Route path="/best-reviews" element={<BestReviews />} />
              <Route
                path="/reviewshow/:product_code"
                element={<Showallreviews />}
              />
              <Route path="/search/:query" element={<Search />} />

              {/* Anything else renders a real 404 instead of a blank page. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      {!isCheckOutPage && <Footer />}
    </>
  );
}

export default App;
